const PGAdapter = require('./../../common/pg-adapter')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')
const { SHIPMENT_STATUS } = require('../constants')
const { buildOtp } = require('../utils/utils')

const DATA_SHIPMENTSNAPSHOT_TABLE_NAME = 'avocado.data_shipmentsnapshot'
const DATA_SHIPMENTSNAPSHOT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'origin_id',
  'destination_id',
  'notes',
  'is_automatic_return',
  'parent_doc_id',
  'order_id',
  'created_at',
  'updated_at',
  'created_by',
  'couchdb_id',
  'suborder_id',
  'is_gps_authenticated',
  'gps_validation_fail_comment',
  'gps_validation',
  'raw_order_id'
]

const DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME = 'avocado.data_shipmentsnapshot_change'
const DATA_SHIPMENT_COMMENT_TABLE_NAME = 'avocado.data_shipment_comment'

class ShipmentSnapshotAdapter extends PGAdapter {
  constructor (pgConnection, username, logger, options = {}) {
    super(
      pgConnection,
      DATA_SHIPMENTSNAPSHOT_TABLE_NAME,
      username,
      DATA_SHIPMENTSNAPSHOT_COLUMNS,
      'id',
      null,
      logger,
      { strictColumns: false, withGlobalAccessUser: options.withGlobalAccessUser }
    )
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string (e.g., '2024-01-01T00:00:00.000Z')
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds (origins + destinations)
   */
  async getCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!Array.isArray(routeIds)) {
      throw new Error('routeIds must be an array')
    }
    if (routeIds.length === 0) {
      return { couchdbIds: [], locationIds: [], funderIds: [], serviceIds: [], productIds: [] }
    }

    const { programIds = [], cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let query = `
      SELECT ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.couchdb_id IS NOT NULL
        AND ss.route_id = ANY($${paramIndex++}::text[])
    `

    params.push(routeIds)

    if (programIds.length > 0) {
      query += ` AND ss.program_id = ANY($${paramIndex++}::text[])`
      params.push(programIds)
    }

    if (cutoffDate) {
      query += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    // We add the Group by clause after adding potential filters
    query += ` GROUP BY ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, s.fsid`

    try {
      const result = await this.pgConnection.query(query, params)

      const couchdbIds = result.rows.map(row => row.couchdb_id)
      // Dedupe location IDs because multiple shipments may share the same origin/destination.
      // These IDs are used for offline locationMatch filtering in the van-shipments dispenser.
      const locationSet = new Set()
      const skuSet = []
      const funderSet = new Set()
      const serviceSet = new Set()

      result.rows.forEach(row => {
        if (row.origin_id) locationSet.add(row.origin_id)
        if (row.destination_id) locationSet.add(row.destination_id)
        if (row.skus && Array.isArray(row.skus)) {
          skuSet.push(...row.skus)
        }
        if (row.funder_id) funderSet.add(row.funder_id)
        if (row.service_id) serviceSet.add(row.service_id)
      })

      return {
        couchdbIds,
        locationIds: Array.from(locationSet),
        funderIds: Array.from(funderSet),
        serviceIds: Array.from(serviceSet),
        productIds: [...new Set(skuSet)]
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-query-failed',
          routeIds,
          programIds,
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get CouchDB IDs, location IDs, funder IDs, service IDs, product IDs, and change doc IDs
   * for shipment snapshots grouped by route ID.
   * Aggregates per route on the server and returns one row per route.
   * The previous per-snapshot version shipped one row per snapshot (with
   * repeated route metadata and per-snapshot sku/batch arrays), which on large
   * datasets meant streaming hundreds of MB into the Lambda only to collapse
   * it to one entry per route in JS.
   * @param {object} options - Query options
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @returns {Promise<Object<string, {couchdbIds: string[], locationIds: string[], funderIds: string[], serviceIds: string[], productIds: string[], changeDocIds: string[]}>>} Map of routeId -> route data
   */
  async getCouchdbIdsByRoutesGrouped (options = {}) {
    const { cutoffDate = null } = options

    try {
      // Keep a snapshot in the window if EITHER it OR its parent route was
      // touched recently. `assignCarrier` (and other route-only mutations)
      // bumps `data_route.updated_at` but not the snapshots', so checking
      // only snapshots would silently drop a route's shipments after its
      // snapshots aged out even though the route itself is current.
      // Active routes are pre-computed into a temp table (combining both
      // snapshot and route activity) within a single transaction to ensure
      // the temp table is visible to the main query even through pgbouncer
      // in transaction mode.
      let params = []
      let cutoffClause = ''
      let client = null

      if (cutoffDate) {
        client = await this.pgConnection.connect()
        await client.query('BEGIN')

        // The temp-table setup is intentionally split into four separate client.query()
        // calls rather than one multi-statement string. node-postgres uses the extended
        // (prepared) query protocol whenever params are passed, and that protocol does
        // not allow multiple statements in a single call. Splitting keeps each statement
        // simple: only the CREATE TEMP TABLE call passes params ($1 = cutoffDate);
        // the others use the simple protocol with no params.
        await client.query('DROP TABLE IF EXISTS tmp_active_routes')
        await client.query(`
          CREATE TEMP TABLE tmp_active_routes AS
          SELECT route_id
          FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME}
          WHERE updated_at >= $1
          AND route_id IS NOT NULL
          AND couchdb_id IS NOT NULL
          UNION
          SELECT couchdb_id AS route_id
          FROM avocado.data_route
          WHERE updated_at >= $1
          AND couchdb_id IS NOT NULL
        `, [cutoffDate])
        await client.query('CREATE INDEX ON tmp_active_routes (route_id)')
        cutoffClause = `
            AND ss.route_id IN (SELECT route_id FROM tmp_active_routes)`
      }

      // The filtered snapshot set is materialized once and shared by every
      // aggregation, so the big snapshot table is scanned a single time.
      // Each follow-up CTE groups per route; the line/details fan-out stays
      // inside `products` and never multiplies the snapshot-level rows.
      const query = `
        WITH snapshots AS MATERIALIZED (
          SELECT ss.id, ss.route_id, ss.couchdb_id, ss.shipment_id,
                 ss.origin_id, ss.destination_id, ss.funder_id,
                 ss.program_id, ss.updated_at, r.carrier_id
          FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
          LEFT JOIN avocado.data_route r ON ss.route_id = r.couchdb_id
          WHERE ss.route_id IS NOT NULL
            AND ss.couchdb_id IS NOT NULL${cutoffClause}
        ),
        routes AS (
          SELECT route_id,
                 MAX(carrier_id::text) AS carrier_id,
                 MAX(updated_at) AS last_updated,
                 ARRAY_AGG(DISTINCT couchdb_id) AS couchdb_ids,
                 ARRAY_AGG(DISTINCT origin_id) FILTER (WHERE origin_id IS NOT NULL) AS origin_ids,
                 ARRAY_AGG(DISTINCT destination_id) FILTER (WHERE destination_id IS NOT NULL) AS destination_ids,
                 ARRAY_AGG(DISTINCT funder_id) FILTER (WHERE funder_id IS NOT NULL) AS funder_ids
          FROM snapshots
          GROUP BY route_id
        ),
        services AS (
          SELECT s.route_id,
                 ARRAY_AGG(DISTINCT sv.fsid) AS service_ids
          FROM snapshots s
          JOIN avocado.data_program p ON s.program_id = p.fsid
          JOIN avocado.data_service sv ON p.id = sv.program_id
          WHERE sv.fsid IS NOT NULL
          GROUP BY s.route_id
        ),
        products AS (
          SELECT s.route_id,
                 ARRAY_AGG(DISTINCT 'product:' || ssl.sku) FILTER (WHERE ssl.sku IS NOT NULL) AS product_ids,
                 ARRAY_AGG(DISTINCT ssld.batch) FILTER (WHERE ssld.batch IS NOT NULL) AS batch_ids
          FROM snapshots s
          JOIN avocado.data_shipmentsnapshotline ssl ON s.id = ssl.shipment_snapshot_id
          LEFT JOIN avocado.data_shipmentsnapshotline_details ssld ON ssl.id = ssld.shipmentsnapshot_line
          GROUP BY s.route_id
        ),
        changes AS (
          SELECT s.route_id,
                 ARRAY_AGG(DISTINCT sc.couchdb_id) FILTER (WHERE sc.couchdb_id IS NOT NULL) AS change_doc_ids
          FROM snapshots s
          JOIN ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc ON sc.snapshot_couchdb_id = s.couchdb_id
          GROUP BY s.route_id
        ),
        shipments AS (
          SELECT DISTINCT route_id, shipment_id
          FROM snapshots
          WHERE shipment_id IS NOT NULL
        ),
        otps AS (
          SELECT sh.route_id,
                 ARRAY_AGG(DISTINCT so.couchdb_id) AS otp_doc_ids
          FROM shipments sh
          JOIN avocado.shipment_otp so ON so.shipment_id = sh.shipment_id
          WHERE so.couchdb_id IS NOT NULL
          GROUP BY sh.route_id
        ),
        comments AS (
          SELECT sh.route_id,
                 ARRAY_AGG(DISTINCT sc.couchdb_id) AS comment_doc_ids
          FROM shipments sh
          JOIN ${DATA_SHIPMENT_COMMENT_TABLE_NAME} sc ON sc.shipment_id = sh.shipment_id
          WHERE sc.couchdb_id IS NOT NULL
          GROUP BY sh.route_id
        )
        SELECT r.route_id,
               r.carrier_id,
               r.last_updated,
               r.couchdb_ids,
               r.origin_ids,
               r.destination_ids,
               r.funder_ids,
               sv.service_ids,
               p.product_ids,
               p.batch_ids,
               c.change_doc_ids,
               o.otp_doc_ids,
               cm.comment_doc_ids
        FROM routes r
        LEFT JOIN services sv USING (route_id)
        LEFT JOIN products p USING (route_id)
        LEFT JOIN changes c USING (route_id)
        LEFT JOIN otps o USING (route_id)
        LEFT JOIN comments cm USING (route_id)`

      // Run the main query on the same client (inside the transaction) if a
      // cutoffDate was given, otherwise use the pool directly.
      const result = client
        ? await client.query(query, params)
        : await this.pgConnection.query(query, params)

      if (client) {
        await client.query('COMMIT')
        client.release()
      }

      const routeMap = {}
      for (const row of result.rows) {
        routeMap[row.route_id] = {
          carrierId: row.carrier_id || null,
          couchdbIds: row.couchdb_ids || [],
          commentDocIds: row.comment_doc_ids || [],
          locationIds: Array.from(new Set([
            ...(row.origin_ids || []),
            ...(row.destination_ids || [])
          ])),
          funderIds: row.funder_ids || [],
          serviceIds: row.service_ids || [],
          productIds: row.product_ids || [],
          changeDocIds: row.change_doc_ids || [],
          otpDocIds: row.otp_doc_ids || [],
          lastUpdated: row.last_updated,
          batchIds: row.batch_ids || []
        }
      }

      return routeMap
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-grouped-query-failed',
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get LMD snapshot enrichment data from PostgreSQL.
   *
   * @param {string[]} shipmentIds - Array of shipment_id values
   * @returns {Promise<Object[]>} Array of enrichment objects keyed by shipment_id
   */
  async getLmdSnapshotData (shipmentIds) {
    if (!shipmentIds.length) return []

    const snapshotQuery = `
      SELECT
        ss.shipment_id,
        ss.couchdb_id,
        ss.status,
        ss.created_at,
        ss.created_by,
        ss.parent_doc_id,
        ss.raw_order_id,
        ss.is_gps_authenticated,
        ss.gps_validation_fail_comment,
        ss.gps_validation,
        (
          SELECT jsonb_object_agg(
            ssld.batch,
            jsonb_build_object('quantity', ssld.amount)
          )
          FROM avocado.data_shipmentsnapshotline ssl
          JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
          WHERE ssl.shipment_snapshot_id = ss.id
        ) AS counts
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      WHERE ss.shipment_id = ANY($1)
      ORDER BY ss.shipment_id, ss.created_at ASC
    `

    const otpQuery = `
      SELECT
        so.shipment_id,
        so.password,
        so.otp_key,
        so.user_fail_reason,
        so.verified
      FROM avocado.shipment_otp so
      WHERE so.shipment_id = ANY($1)
    `

    const [snapshotResult, otpResult] = await Promise.all([
      this.pgConnection.query(snapshotQuery, [shipmentIds]),
      this.pgConnection.query(otpQuery, [shipmentIds])
    ])

    const otpByShipment = {}
    for (const row of otpResult.rows) {
      otpByShipment[row.shipment_id] = row
    }

    const shipmentMap = {}
    for (const row of snapshotResult.rows) {
      const entry = shipmentMap[row.shipment_id] || { snapshotDates: {}, history: {} }
      shipmentMap[row.shipment_id] = entry

      const createdAtISO = row.created_at.toISOString()

      // snapshotDates: earliest date wins per status
      if (!entry.snapshotDates[row.status] || createdAtISO < entry.snapshotDates[row.status]) {
        entry.snapshotDates[row.status] = createdAtISO
      }

      // history: keyed by couchdb_id
      if (row.couchdb_id) {
        entry.history[row.couchdb_id] = omitBy({
          createdAt: createdAtISO,
          createdBy: { user: row.created_by, agent: '' },
          counts: row.counts || {},
          parentDocId: row.parent_doc_id
        }, isNil)
      }

      entry.orderId = row.raw_order_id
      entry.isGpsAuthenticated = row.is_gps_authenticated
      entry.gpsValidationFailComment = row.gps_validation_fail_comment
      entry.gpsValidation = row.gps_validation

      if (row.status === SHIPMENT_STATUS.RECEIVED) {
        entry.driverName = row.created_by
      }
    }

    return shipmentIds.map(shipmentId => {
      const entry = shipmentMap[shipmentId]
      if (!entry) return null

      const otpRow = otpByShipment[shipmentId]
      if (otpRow) {
        entry.otp = buildOtp(otpRow)
      }

      return { id: shipmentId, ...entry }
    }).filter(Boolean)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getChangeDocIds (snapshotCouchdbIds) {
    if (!Array.isArray(snapshotCouchdbIds) || snapshotCouchdbIds.length === 0) {
      return []
    }

    const query = `
      SELECT sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    try {
      const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
      return result.rows.map(row => row.couchdb_id)
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-change-query-failed',
          snapshotCouchdbIds,
          error: error.message
        })
      }
      throw error
    }
  }
}

module.exports = ShipmentSnapshotAdapter
